/**
 * MesaDecisao - Matriz item×fornecedor para escolher vencedor por linha (Fase 4).
 *
 * Cada item da cotação pode ter um fornecedor vencedor distinto.
 * Trocar uma decisão já existente requer motivo (auditoria).
 *
 * Props:
 *  - cotacao: objeto da cotação carregada
 *  - onReload?: callback após decisões mudarem
 *  - onPedidosGerados?: callback após gerar pedidos
 *  - onClose?: fecha modal
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import api from '../../lib/axios';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Progress } from '../ui/progress';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import {
  Check, Trophy, AlertTriangle, Loader2, FileText, Sparkles, X, RotateCcw, Package, Truck, Split, Edit, Users,
  Award, ChevronDown, ChevronUp, RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency as _fmtCurrency } from './utils';
import { getCurrencyMeta } from '../../lib/currencies';
import PdfPreviewDialog from '../PdfPreviewDialog';
import AvaliarAlternativaDialog from './AvaliarAlternativaDialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

const MesaDecisao = ({ cotacao, onReload, onPedidosGerados, onClose, onEditCotacao, onRegistrarResposta }) => {
  // Todas as comparações usam a moeda da cotação (sem conversão)
  const formatCurrency = (v) => _fmtCurrency(v, cotacao?.moeda || 'BRL');
  const [decisoes, setDecisoes] = useState([]);  // [{item_idx, decisao, ...}]
  const [resumo, setResumo] = useState({ total_itens: 0, decididos: 0, pendentes: 0, completo: false, fornecedores_vencedores: [] });
  const [pedidosGerados, setPedidosGerados] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);

  // Modal de motivo (troca de decisão)
  const [trocaDecisao, setTrocaDecisao] = useState(null); // {item_idx, forn_id, forn_nome, preco_final, decisao_atual}
  const [motivoTroca, setMotivoTroca] = useState('');

  // Modal de split de quantidade (Fase 4.4b)
  // splitItem = {item_idx, item, splits: [{forn_id, quantidade, preco_final}]}
  const [splitItem, setSplitItem] = useState(null);
  const [motivoSplit, setMotivoSplit] = useState('');

  // Supplier Score (migrado do antigo ComparativoRespostas)
  const [scores, setScores] = useState(null);
  const [criterios, setCriterios] = useState([]);
  const [avaliacoes, setAvaliacoes] = useState({});
  const [loadingScore, setLoadingScore] = useState(false);
  const [showCriteriosManuais, setShowCriteriosManuais] = useState(false);
  const [showBreakdown, setShowBreakdown] = useState(false);

  // Preview de PDF da resposta do fornecedor
  const [pdfPreview, setPdfPreview] = useState(null); // { path, filename, title }

  // Inteligência do negócio: histórico dos fornecedores (badges de contexto)
  const [intelFornecedores, setIntelFornecedores] = useState({});
  useEffect(() => {
    api.get('/compras/assistente/inteligencia')
      .then((r) => {
        const map = {};
        (r.data?.fornecedores || []).forEach((f) => { map[f.fornecedor_id] = f; });
        setIntelFornecedores(map);
      })
      .catch(() => {});
  }, []);

  // Avaliar alternativa sugerida pelo fornecedor (modal de adoção)
  const [avaliarAlternativa, setAvaliarAlternativa] = useState(null);
  // { itemIdx, item, fornecedor, info }

  const itens = cotacao?.itens || [];
  const fornecedores = (cotacao?.fornecedores || []).filter(f => f.resposta || f.status === 'respondido');

  // Build mapa de preços: { [item_id]: { [forn_id]: {preco_unitario, item_resp} } }
  const precosPorItem = useMemo(() => {
    const map = {};
    itens.forEach(item => {
      map[item.id] = {};
      fornecedores.forEach(forn => {
        const itemResp = (forn.resposta?.itens || []).find(ir => ir.item_id === item.id);
        if (itemResp && (itemResp.preco_unitario || 0) > 0) {
          map[item.id][forn.fornecedor_id] = {
            preco_unitario: itemResp.preco_unitario,
            prazo_dias: itemResp.prazo_dias,
            disponivel: itemResp.disponivel !== false,
            // Sugestão de alternativa enviada pelo fornecedor (texto livre)
            descricao_alternativa: itemResp.descricao_alternativa || '',
            marca_alternativa: itemResp.marca_alternativa || '',
            motivo_alternativa: itemResp.motivo_alternativa || '',
            // Equivalente JÁ ADOTADO pelo comprador para ESTE fornecedor
            equivalente_adotado: !!itemResp.equivalente_adotado,
            equivalente: itemResp.equivalente || null,
          };
        }
      });
    });
    return map;
  }, [itens, fornecedores]);

  // Menor preço por item (para highlight)
  const melhorPorItem = useMemo(() => {
    const best = {};
    Object.entries(precosPorItem).forEach(([itemId, precosObj]) => {
      let min = null;
      let fornId = null;
      Object.entries(precosObj).forEach(([fid, info]) => {
        if (min === null || info.preco_unitario < min) {
          min = info.preco_unitario;
          fornId = fid;
        }
      });
      best[itemId] = { fornecedor_id: fornId, preco: min };
    });
    return best;
  }, [precosPorItem]);

  // Menor Landed Cost entre os fornecedores (destaque do vencedor por custo total)
  // Landed = total + frete(FOB) + impostos(não inclusos) − desconto
  const menorLanded = useMemo(() => {
    let min = null;
    fornecedores.forEach(forn => {
      const total = forn.resposta?.total || forn.resposta?.valor_total || 0;
      const freteTipo = forn.resposta?.frete_tipo;
      const freteValor = Number(forn.resposta?.frete_valor) || 0;
      const impostos = Number(forn.resposta?.valor_impostos) || 0;
      const impostosInclusos = !!forn.resposta?.impostos_inclusos;
      const desconto = Number(forn.resposta?.valor_desconto || forn.resposta?.desconto) || 0;
      const freteAdd = freteTipo === 'fob' ? freteValor : 0;
      const impostosAdd = impostosInclusos ? 0 : impostos;
      const landed = total + freteAdd + impostosAdd - desconto;
      if (landed > 0 && (min === null || landed < min)) min = landed;
    });
    return min === null ? 0 : min;
  }, [fornecedores]);


  const fetchDecisoes = useCallback(async () => {
    if (!cotacao?.id) return;
    setLoading(true);
    try {
      const r = await api.get(`/cotacoes/${cotacao.id}/decisoes`);
      setDecisoes(r.data?.decisoes || []);
      setResumo(r.data?.resumo || resumo);
      setPedidosGerados(r.data?.pedidos_gerados || []);
    } catch (err) {
      toast.error('Erro ao carregar decisões');
    } finally {
      setLoading(false);
    }
  }, [cotacao?.id]); // eslint-disable-line

  useEffect(() => { fetchDecisoes(); }, [fetchDecisoes]);

  // Carrega Supplier Score (migrado do ComparativoRespostas)
  const calcularScores = useCallback(async () => {
    if (!cotacao?.id) return;
    setLoadingScore(true);
    try {
      const res = await api.post(`/cotacoes/${cotacao.id}/supplier-score`, { avaliacoes });
      setScores(res.data?.scores || []);
      setCriterios(res.data?.criterios || []);
    } catch (e) {
      // silent — Score é opcional
    } finally {
      setLoadingScore(false);
    }
    // eslint-disable-next-line
  }, [cotacao?.id]);

  useEffect(() => { calcularScores(); }, [calcularScores]);

  const updateAvaliacao = (fornId, criterioId, valor) => {
    setAvaliacoes(prev => ({
      ...prev,
      [fornId]: { ...(prev[fornId] || {}), [criterioId]: valor }
    }));
  };

  const recalcularScore = async () => {
    setLoadingScore(true);
    try {
      const res = await api.post(`/cotacoes/${cotacao.id}/supplier-score`, { avaliacoes });
      setScores(res.data?.scores || []);
      setCriterios(res.data?.criterios || []);
    } catch (e) { /* silent */ }
    finally { setLoadingScore(false); }
  };

  const criteriosManuais = useMemo(() => criterios.filter(c => c.tipo === 'manual' && c.ativo), [criterios]);
  const melhorScore = scores ? Math.max(...scores.map(s => s.score)) : 0;
  const criteriosAtivos = useMemo(() =>
    (scores?.[0]?.breakdown || []).filter(b => {
      const crit = criterios.find(c => c.id === b.criterio_id);
      return crit?.ativo && crit?.peso > 0;
    }), [scores, criterios]
  );

  const getDecisaoDoItem = (itemIdx) => {
    const d = decisoes.find(x => x.item_idx === itemIdx);
    return d?.decisao || null;
  };

  const aplicarDecisao = async (itemIdx, fornId, fornNome, precoFinal, motivo = '') => {
    try {
      const item = itens[itemIdx];
      await api.put(`/cotacoes/${cotacao.id}/itens/${itemIdx}/decisao`, {
        fornecedor_vencedor_id: fornId,
        fornecedor_vencedor_nome: fornNome,
        preco_final: precoFinal,
        quantidade_final: item?.quantidade || 1,
        motivo,
      });
      toast.success('Decisão registrada');
      await fetchDecisoes();
      if (onReload) onReload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao registrar decisão');
    }
  };

  const handleSelecionar = (itemIdx, fornId) => {
    const item = itens[itemIdx];
    const forn = fornecedores.find(f => f.fornecedor_id === fornId);
    const info = precosPorItem[item.id]?.[fornId];
    if (!info) {
      toast.error('Fornecedor não cotou este item');
      return;
    }
    const decisaoAtual = getDecisaoDoItem(itemIdx);
    // Se já existe e é DIFERENTE, exige motivo
    if (decisaoAtual?.fornecedor_vencedor_id && decisaoAtual.fornecedor_vencedor_id !== fornId) {
      setTrocaDecisao({
        item_idx: itemIdx,
        forn_id: fornId,
        forn_nome: forn?.fornecedor_nome || '',
        preco_final: info.preco_unitario,
        decisao_atual: decisaoAtual,
      });
      setMotivoTroca('');
      return;
    }
    // Toggle: se já é este, limpa
    if (decisaoAtual?.fornecedor_vencedor_id === fornId) {
      handleLimpar(itemIdx);
      return;
    }
    aplicarDecisao(itemIdx, fornId, forn?.fornecedor_nome || '', info.preco_unitario);
  };

  const confirmarTroca = async () => {
    if (!trocaDecisao) return;
    if (!motivoTroca.trim()) {
      toast.error('Informe o motivo da troca');
      return;
    }
    await aplicarDecisao(
      trocaDecisao.item_idx,
      trocaDecisao.forn_id,
      trocaDecisao.forn_nome,
      trocaDecisao.preco_final,
      motivoTroca.trim()
    );
    setTrocaDecisao(null);
    setMotivoTroca('');
  };

  const handleLimpar = async (itemIdx) => {
    if (!window.confirm('Limpar decisão deste item?')) return;
    try {
      await api.delete(`/cotacoes/${cotacao.id}/itens/${itemIdx}/decisao?motivo=removida via Mesa de Decisão`);
      toast.success('Decisão removida');
      await fetchDecisoes();
      if (onReload) onReload();
    } catch (err) {
      toast.error('Erro ao limpar');
    }
  };

  // Abrir modal de split: inicializa com 50/50 entre os 2 fornecedores mais baratos
  const abrirSplit = (itemIdx) => {
    const item = itens[itemIdx];
    if (!item) return;
    const precosObj = precosPorItem[item.id] || {};
    const ordenados = Object.entries(precosObj)
      .map(([fid, info]) => ({ fid, preco: info.preco_unitario }))
      .sort((a, b) => a.preco - b.preco);
    if (ordenados.length < 2) {
      toast.error('São necessários pelo menos 2 fornecedores que cotaram este item para fazer split');
      return;
    }
    const half = (item.quantidade || 1) / 2;
    setSplitItem({
      item_idx: itemIdx,
      item,
      splits: ordenados.slice(0, 2).map(s => ({
        fornecedor_vencedor_id: s.fid,
        quantidade: Math.round(half * 100) / 100,
        preco_final: s.preco,
      })),
    });
    setMotivoSplit('');
  };

  const fecharSplit = () => { setSplitItem(null); setMotivoSplit(''); };

  const updateSplitRow = (idx, field, value) => {
    if (!splitItem) return;
    const novos = [...splitItem.splits];
    novos[idx] = { ...novos[idx], [field]: value };
    // Se mudou fornecedor, atualizar preco_final automaticamente
    if (field === 'fornecedor_vencedor_id') {
      const item = splitItem.item;
      const info = precosPorItem[item.id]?.[value];
      if (info) novos[idx].preco_final = info.preco_unitario;
    }
    setSplitItem({ ...splitItem, splits: novos });
  };

  const addSplitRow = () => {
    if (!splitItem) return;
    // Pega um fornecedor que cotou e ainda não está no split
    const usados = new Set(splitItem.splits.map(s => s.fornecedor_vencedor_id));
    const item = splitItem.item;
    const candidatos = Object.entries(precosPorItem[item.id] || {})
      .filter(([fid]) => !usados.has(fid));
    if (candidatos.length === 0) {
      toast.error('Sem mais fornecedores que cotaram para adicionar');
      return;
    }
    const [fid, info] = candidatos[0];
    setSplitItem({
      ...splitItem,
      splits: [...splitItem.splits, {
        fornecedor_vencedor_id: fid,
        quantidade: 1,
        preco_final: info.preco_unitario,
      }],
    });
  };

  const removeSplitRow = (idx) => {
    if (!splitItem) return;
    if (splitItem.splits.length <= 2) {
      toast.error('Split precisa ter pelo menos 2 fornecedores');
      return;
    }
    setSplitItem({ ...splitItem, splits: splitItem.splits.filter((_, i) => i !== idx) });
  };

  const confirmarSplit = async () => {
    if (!splitItem) return;
    const item = splitItem.item;
    const somaQtd = splitItem.splits.reduce((acc, s) => acc + (Number(s.quantidade) || 0), 0);
    const qtdTotal = Number(item.quantidade) || 0;
    if (Math.abs(somaQtd - qtdTotal) > 0.01) {
      toast.error(`Soma das quantidades (${somaQtd}) deve ser igual a ${qtdTotal}`);
      return;
    }
    // Valida cada split tem preço
    for (const s of splitItem.splits) {
      if (!s.fornecedor_vencedor_id || !(Number(s.preco_final) > 0)) {
        toast.error('Cada linha do split precisa de fornecedor e preço > 0');
        return;
      }
    }
    // Se já existia decisão diferente, motivo obrigatório
    const decisaoAtual = getDecisaoDoItem(splitItem.item_idx);
    const tinha = decisaoAtual && (decisaoAtual.fornecedor_vencedor_id || decisaoAtual.is_split);
    if (tinha && !motivoSplit.trim()) {
      toast.error('Motivo é obrigatório ao alterar a decisão de um item já definido');
      return;
    }
    try {
      // Enriquece com nome
      const splitsBody = splitItem.splits.map(s => {
        const forn = fornecedores.find(f => f.fornecedor_id === s.fornecedor_vencedor_id);
        return {
          fornecedor_vencedor_id: s.fornecedor_vencedor_id,
          fornecedor_vencedor_nome: forn?.fornecedor_nome,
          quantidade: Number(s.quantidade),
          preco_final: Number(s.preco_final),
        };
      });
      await api.put(`/cotacoes/${cotacao.id}/itens/${splitItem.item_idx}/decisao/split`, {
        splits: splitsBody,
        motivo: motivoSplit.trim(),
      });
      toast.success(`Split aplicado: ${splitsBody.length} fornecedores`);
      fecharSplit();
      await fetchDecisoes();
      if (onReload) onReload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao salvar split');
    }
  };

  // Aprovar TODOS os itens onde aquele fornecedor cotou (que ainda não têm decisão nem pedido).
  // Substitui o antigo "Aprovar Cotação Inteira" do ComparativoRespostas, mas item por item.
  const handleAprovarTudoFornecedor = async (fornId, fornNome) => {
    const itensElegiveis = [];
    itens.forEach((item, idx) => {
      const decisaoAtual = getDecisaoDoItem(idx);
      if (decisaoAtual?.fornecedor_vencedor_id || decisaoAtual?.is_split) return;
      const temPedidoGerado = !!(item.pedido_gerado_id || (item.pedidos_gerados_ids && item.pedidos_gerados_ids.length > 0));
      if (temPedidoGerado) return;
      const info = precosPorItem[item.id]?.[fornId];
      if (!info) return;
      itensElegiveis.push({ idx, item, info });
    });
    if (itensElegiveis.length === 0) {
      toast.info(`Nenhum item disponível para premiar ${fornNome} (sem cotação ou já decidido).`);
      return;
    }
    if (!window.confirm(`Aprovar ${itensElegiveis.length} item(ns) de ${fornNome} de uma vez?\n(Itens já decididos não serão alterados.)`)) return;
    let aplicadas = 0;
    for (const { idx, item, info } of itensElegiveis) {
      try {
        await api.put(`/cotacoes/${cotacao.id}/itens/${idx}/decisao`, {
          fornecedor_vencedor_id: fornId,
          fornecedor_vencedor_nome: fornNome,
          preco_final: info.preco_unitario,
          quantidade_final: item?.quantidade || 1,
          motivo: `aprovação em lote: ${fornNome}`,
        });
        aplicadas++;
      } catch (e) { /* segue */ }
    }
    await fetchDecisoes();
    if (onReload) onReload();
    toast.success(`${aplicadas} item(ns) atribuído(s) a ${fornNome}`);
  };

  const handleSugerirMelhorPreco = async () => {
    if (!window.confirm('Aplicar a sugestão de melhor preço em TODOS os itens sem decisão?\n(Itens já decididos não serão alterados.)')) return;
    let aplicadas = 0;
    for (let idx = 0; idx < itens.length; idx++) {
      const item = itens[idx];
      const decisaoAtual = getDecisaoDoItem(idx);
      if (decisaoAtual?.fornecedor_vencedor_id) continue;
      const best = melhorPorItem[item.id];
      if (!best?.fornecedor_id) continue;
      const forn = fornecedores.find(f => f.fornecedor_id === best.fornecedor_id);
      try {
        await api.put(`/cotacoes/${cotacao.id}/itens/${idx}/decisao`, {
          fornecedor_vencedor_id: best.fornecedor_id,
          fornecedor_vencedor_nome: forn?.fornecedor_nome || '',
          preco_final: best.preco,
          quantidade_final: item?.quantidade || 1,
          motivo: 'sugestão automática: melhor preço',
        });
        aplicadas++;
      } catch (e) { /* segue */ }
    }
    await fetchDecisoes();
    if (onReload) onReload();
    toast.success(`${aplicadas} decisões aplicadas pela sugestão de melhor preço`);
  };

  const handleGerarPedidos = async () => {
    const decididosSemPedido = resumo.decididos_sem_pedido_gerenciavel ?? resumo.decididos_sem_pedido ?? 0;
    const pendentes = resumo.pendentes || 0;
    if (decididosSemPedido === 0) {
      toast.error('Decida pelo menos 1 item antes de gerar pedidos');
      return;
    }
    // Confirmação inteligente: explicita se é parcial
    const fornecedoresSemPedidoUnicos = new Set();
    decisoes.forEach(d => {
      const item = itens[d.item_idx];
      if (item.pedido_gerado_id || (item.pedidos_gerados_ids && item.pedidos_gerados_ids.length > 0)) return;
      const dec = d.decisao;
      if (!dec) return;
      if (dec.is_split) {
        (dec.splits || []).forEach(s => fornecedoresSemPedidoUnicos.add(s.fornecedor_vencedor_id));
      } else if (dec.fornecedor_vencedor_id) {
        fornecedoresSemPedidoUnicos.add(dec.fornecedor_vencedor_id);
      }
    });
    const numPedidos = fornecedoresSemPedidoUnicos.size;
    const ehParcial = pendentes > 0;
    const msg = ehParcial
      ? `Você vai gerar ${numPedidos} pedido(s) nesta onda parcial, deixando ${pendentes} item(ns) em aberto para futuras decisões.\n\nApós a geração, esses itens ficam travados e não poderão ter a decisão alterada (a menos que o pedido seja cancelado).\n\nConfirmar?`
      : `Confirmar geração de ${numPedidos} pedido(s)?\nEsta é a onda final — cotação será marcada como aprovada.`;
    if (!window.confirm(msg)) return;

    setGenerating(true);
    try {
      const r = await api.post(`/cotacoes/${cotacao.id}/gerar-pedidos`);
      const totalGerados = r.data?.total_pedidos || 0;
      const novoStatus = r.data?.novo_status;
      const avisoAprov = r.data?.aviso_aprovacao;
      toast.success(novoStatus === 'aprovada_parcial'
        ? `Onda parcial: ${totalGerados} pedido(s) gerado(s). ${pendentes} item(ns) ainda em aberto.`
        : `${totalGerados} pedido(s) gerado(s) — cotação aprovada!`);
      if (avisoAprov) {
        toast.warning(avisoAprov, { duration: 8000 });
      }
      if (onPedidosGerados) onPedidosGerados(r.data);
      if (onReload) onReload();
      await fetchDecisoes();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao gerar pedidos');
    } finally {
      setGenerating(false);
    }
  };

  // Total estimado do lote
  const totalEstimado = decisoes.reduce((acc, d) => {
    const dec = d.decisao;
    if (!dec) return acc;
    return acc + (Number(dec.preco_final) || 0) * (Number(dec.quantidade_final) || 1);
  }, 0);

  // Quantos pedidos serão gerados
  const fornecedoresVencedoresUnicos = new Set();
  decisoes.forEach(d => {
    const fid = d.decisao?.fornecedor_vencedor_id;
    if (fid) fornecedoresVencedoresUnicos.add(fid);
  });

  // ALERTA DE FRETE MÍNIMO (Fase 4.4a)
  // Para cada fornecedor escolhido, soma o subtotal das decisões dele
  // e compara com `resposta.frete_minimo` (campo livre informado pelo fornecedor).
  const alertasFreteMinimo = useMemo(() => {
    const subtotalPorForn = {};
    decisoes.forEach(d => {
      const dec = d.decisao;
      if (!dec?.fornecedor_vencedor_id) return;
      const fid = dec.fornecedor_vencedor_id;
      const sub = (Number(dec.preco_final) || 0) * (Number(dec.quantidade_final) || 1);
      subtotalPorForn[fid] = (subtotalPorForn[fid] || 0) + sub;
    });
    const alertas = [];
    Object.entries(subtotalPorForn).forEach(([fid, subtotal]) => {
      const forn = fornecedores.find(f => f.fornecedor_id === fid);
      const fMin = Number(forn?.resposta?.frete_minimo) || 0;
      if (fMin > 0 && subtotal < fMin) {
        // Sugerir item adicional onde esse fornecedor é o 2º mais barato e ainda não foi decidido
        let sugestao = null;
        let menorDif = Infinity;
        itens.forEach((item, idx) => {
          const dec = getDecisaoDoItem(idx);
          if (dec?.fornecedor_vencedor_id) return;
          const precosObj = precosPorItem[item.id] || {};
          if (!precosObj[fid]) return;
          const precos = Object.values(precosObj).map(p => p.preco_unitario);
          const menor = Math.min(...precos);
          const dif = precosObj[fid].preco_unitario - menor;
          if (dif < menorDif) {
            menorDif = dif;
            sugestao = { item_idx: idx, item, dif };
          }
        });
        alertas.push({
          fornecedor_id: fid,
          fornecedor_nome: forn?.fornecedor_nome,
          subtotal,
          frete_minimo: fMin,
          falta: fMin - subtotal,
          sugestao,
        });
      }
    });
    return alertas;
  }, [decisoes, fornecedores, itens, precosPorItem]); // eslint-disable-line

  // ===== AÇÕES DE APROVAÇÃO =====
  const [aprovacaoLoading, setAprovacaoLoading] = useState(false);
  const [showRejeitarDialog, setShowRejeitarDialog] = useState(false);
  const [motivoRejeicao, setMotivoRejeicao] = useState('');
  const [rejeitarItem, setRejeitarItem] = useState(null); // {idx, motivo}

  const handleDecisaoItemAprovador = async (itemIdx, acao, motivo = '') => {
    try {
      await api.post(`/cotacoes/${cotacao.id}/itens/${itemIdx}/decisao-aprovador`, { acao, motivo });
      toast.success(acao === 'aprovar' ? 'Item aprovado' : 'Item rejeitado');
      if (onReload) onReload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao decidir item');
    }
  };

  const handleConfirmarRejeitarItem = async () => {
    if (!rejeitarItem) return;
    if (!rejeitarItem.motivo || rejeitarItem.motivo.trim().length < 3) {
      toast.error('Informe o motivo (mín. 3 caracteres)');
      return;
    }
    await handleDecisaoItemAprovador(rejeitarItem.idx, 'rejeitar', rejeitarItem.motivo.trim());
    setRejeitarItem(null);
  };

  const handleConcluirAprovacao = async () => {
    if (!window.confirm('Concluir a aprovação? Apenas os itens aprovados irão gerar pedido.')) return;
    setAprovacaoLoading(true);
    try {
      const r = await api.post(`/cotacoes/${cotacao.id}/concluir-aprovacao`, { observacao: '' });
      const ri = r.data?.aprovacao?.resultado_itens || {};
      const qtdPedidos = r.data?.pedidos_gerados_qtd || 0;
      const msgPedidos = qtdPedidos > 0 ? ` · ${qtdPedidos} pedido(s) gerado(s)` : '';
      toast.success(`Aprovação concluída: ${ri.aprovados || 0} aprovado(s), ${ri.rejeitados || 0} rejeitado(s)${msgPedidos}`);
      if (onReload) onReload();
      await fetchDecisoes();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao concluir aprovação');
    } finally {
      setAprovacaoLoading(false);
    }
  };

  const handleRemediarItem = async (itemIdx, acao) => {
    const msg = acao === 'descartar'
      ? 'Descartar este item? Ele não gerará pedido e sairá do ciclo.'
      : 'Re-decidir este item? A decisão atual será limpa para você escolher outro fornecedor.';
    if (!window.confirm(msg)) return;
    try {
      await api.post(`/cotacoes/${cotacao.id}/itens/${itemIdx}/remediar`, { acao });
      toast.success(acao === 'descartar' ? 'Item descartado' : 'Item reaberto para nova decisão');
      if (onReload) onReload();
      await fetchDecisoes();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao remediar item');
    }
  };

  const handleSolicitarAprovacao = async () => {
    if ((cotacao?.aprovacao?.responsaveis || []).length === 0) {
      toast.error('Nenhum responsável de aprovação definido. Edite a cotação para adicionar.');
      return;
    }
    if (!window.confirm(
      `Solicitar aprovação para ${(cotacao.aprovacao.responsaveis || []).length} responsável(is)?\n` +
      `Eles receberão alerta no sistema e WhatsApp (quando configurado).`
    )) return;
    setAprovacaoLoading(true);
    try {
      const r = await api.post(`/cotacoes/${cotacao.id}/solicitar-aprovacao`, { observacao: '' });
      toast.success(
        `Aprovação solicitada! ${r.data.whatsapp_enviados || 0} WhatsApp enviado(s) de ${r.data.responsaveis_notificados || 0} responsáveis.`
      );
      if (onReload) onReload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao solicitar aprovação');
    } finally {
      setAprovacaoLoading(false);
    }
  };

  const handleAprovarCotacao = async () => {
    if (!window.confirm('Confirmar aprovação desta cotação?')) return;
    setAprovacaoLoading(true);
    try {
      await api.post(`/cotacoes/${cotacao.id}/aprovar-cotacao`, { observacao: '' });
      toast.success('Cotação aprovada com sucesso!');
      if (onReload) onReload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao aprovar');
    } finally {
      setAprovacaoLoading(false);
    }
  };

  const handleRejeitarCotacao = async () => {
    if (!motivoRejeicao || motivoRejeicao.trim().length < 3) {
      toast.error('Informe um motivo (mín. 3 caracteres)');
      return;
    }
    setAprovacaoLoading(true);
    try {
      await api.post(`/cotacoes/${cotacao.id}/rejeitar-cotacao`, { motivo: motivoRejeicao.trim() });
      toast.success('Cotação rejeitada');
      setShowRejeitarDialog(false);
      setMotivoRejeicao('');
      if (onReload) onReload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao rejeitar');
    } finally {
      setAprovacaoLoading(false);
    }
  };

  const handleCancelarAprovacao = async () => {
    if (!window.confirm('Cancelar a solicitação de aprovação? Você poderá solicitar novamente depois.')) return;
    setAprovacaoLoading(true);
    try {
      await api.post(`/cotacoes/${cotacao.id}/cancelar-aprovacao`);
      toast.success('Solicitação cancelada');
      if (onReload) onReload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao cancelar');
    } finally {
      setAprovacaoLoading(false);
    }
  };

  if (!cotacao) return null;

  const jaGerou = pedidosGerados.length > 0;
  const progressPct = resumo.total_itens > 0 ? Math.round((resumo.decididos / resumo.total_itens) * 100) : 0;

  // ===== Aprovação =====
  const aprovacao = cotacao?.aprovacao || {};
  const responsaveisAprov = aprovacao.responsaveis || [];
  const requerAprovacao = responsaveisAprov.length > 0;
  const statusAprovacao = aprovacao.status || 'nao_solicitada';
  const currentUserLocal = (() => {
    try { return JSON.parse(localStorage.getItem('nautilus_user') || '{}'); }
    catch { return {}; }
  })();
  const sounEuAprovador = requerAprovacao && responsaveisAprov.some(r => r.id === currentUserLocal.id);
  const isAdminUser = currentUserLocal.role === 'admin';
  const valorReferencia = (aprovacao.valor_referencia || totalEstimado || 0);
  const aprovacaoBloqueiaPedidos = requerAprovacao && ['nao_solicitada', 'pendente', 'rejeitada'].includes(statusAprovacao);

  return (
    <Dialog open={!!cotacao} onOpenChange={(o) => { if (!o && onClose) onClose(); }}>
      <DialogContent
        className="max-w-7xl w-[97vw] max-h-[95vh] overflow-y-auto"
        data-testid="mesa-decisao-dialog"
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Trophy className="w-5 h-5 text-amber-500" />
            Comparar Propostas — {cotacao.numero}
            <Badge variant="outline" className="ml-1 border-indigo-300 text-indigo-700 text-xs" data-testid="mesa-moeda-badge">
              {getCurrencyMeta(cotacao.moeda || 'BRL').symbol} {cotacao.moeda || 'BRL'}
            </Badge>
          </DialogTitle>
          <div className="flex items-center justify-between gap-2 mt-1 flex-wrap">
            <div className="text-sm text-gray-500">
              Escolha um fornecedor vencedor por linha. Ao final, gere os pedidos automaticamente.
            </div>
            {!jaGerou && (onEditCotacao || onRegistrarResposta) && (
              <div className="flex items-center gap-2">
                {onEditCotacao && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => onEditCotacao(cotacao)}
                    data-testid="btn-mesa-editar-cotacao"
                  >
                    <Edit className="w-3 h-3 mr-1" /> Editar cotação
                  </Button>
                )}
                {onRegistrarResposta && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="border-blue-300 text-blue-700 hover:bg-blue-50"
                    onClick={() => onRegistrarResposta(cotacao)}
                    data-testid="btn-mesa-registrar-resposta"
                  >
                    <Users className="w-3 h-3 mr-1" /> Registrar resposta / Adicionar fornecedor
                  </Button>
                )}
              </div>
            )}
          </div>
        </DialogHeader>

        {/* Resumo + ações topo */}
        <Card className="p-4 bg-slate-50 border-slate-200">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <div>
              <Label className="text-xs text-gray-500">Decididos</Label>
              <p className="text-2xl font-bold" data-testid="resumo-decididos">
                {resumo.decididos}/{resumo.total_itens}
              </p>
              <Progress value={progressPct} className="h-1.5 mt-1" />
            </div>
            <div>
              <Label className="text-xs text-gray-500">Pendentes</Label>
              <p className={`text-2xl font-bold ${resumo.pendentes > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
                {resumo.pendentes}
              </p>
            </div>
            <div>
              <Label className="text-xs text-gray-500">Fornecedores vencedores</Label>
              <p className="text-2xl font-bold text-blue-600">{fornecedoresVencedoresUnicos.size}</p>
              <p className="text-[10px] text-gray-400">→ {fornecedoresVencedoresUnicos.size} pedido(s)</p>
            </div>
            <div>
              <Label className="text-xs text-gray-500">Total estimado</Label>
              <p className="text-2xl font-bold text-emerald-700">{formatCurrency(totalEstimado)}</p>
            </div>
            <div className="flex items-end justify-end gap-2">
              {!jaGerou && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleSugerirMelhorPreco}
                  disabled={loading || resumo.completo}
                  title="Aplica melhor preço aos itens ainda sem decisão"
                  data-testid="btn-sugerir-melhor-preco"
                >
                  <Sparkles className="w-3 h-3 mr-1" />
                  Sugerir melhor preço
                </Button>
              )}
            </div>
          </div>
        </Card>

        {/* Banner de Aprovação */}
        {requerAprovacao && (
          <Card
            className={`p-3 border-2 ${
              statusAprovacao === 'aprovada' ? 'bg-emerald-50 border-emerald-300' :
              statusAprovacao === 'rejeitada' ? 'bg-red-50 border-red-300' :
              statusAprovacao === 'pendente' ? 'bg-amber-50 border-amber-300' :
              'bg-blue-50 border-blue-300'
            }`}
            data-testid="banner-aprovacao"
          >
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex-1 min-w-[200px]">
                <div className="flex items-center gap-2 mb-1">
                  {statusAprovacao === 'aprovada' && <Check className="w-4 h-4 text-emerald-600" />}
                  {statusAprovacao === 'rejeitada' && <X className="w-4 h-4 text-red-600" />}
                  {statusAprovacao === 'pendente' && <AlertTriangle className="w-4 h-4 text-amber-600" />}
                  {statusAprovacao === 'nao_solicitada' && <FileText className="w-4 h-4 text-blue-600" />}
                  <Label className="text-sm font-semibold">
                    {statusAprovacao === 'aprovada' && 'Cotação Aprovada'}
                    {statusAprovacao === 'rejeitada' && 'Cotação Rejeitada'}
                    {statusAprovacao === 'pendente' && 'Aguardando Aprovação'}
                    {statusAprovacao === 'nao_solicitada' && 'Requer Aprovação'}
                  </Label>
                </div>
                <div className="text-xs text-gray-700 space-y-0.5">
                  <div>
                    <span className="font-medium">Responsáveis:</span>{' '}
                    {responsaveisAprov.map(r => {
                      const limStr = r.limite_aprovacao_brl > 0
                        ? formatCurrency(r.limite_aprovacao_brl)
                        : 'sem limite';
                      return `${r.full_name} (${limStr})`;
                    }).join(', ')}
                  </div>
                  <div>
                    <span className="font-medium">Valor de referência:</span>{' '}
                    <span className="font-mono">{formatCurrency(valorReferencia)}</span>
                  </div>
                  {statusAprovacao === 'aprovada' && (
                    <div className="text-emerald-700 mt-1">
                      Aprovada por <strong>{aprovacao.aprovada_por_nome}</strong> em{' '}
                      {aprovacao.aprovada_em ? new Date(aprovacao.aprovada_em).toLocaleString('pt-BR') : '-'}
                      {aprovacao.aprovada_observacao && <> — "{aprovacao.aprovada_observacao}"</>}
                    </div>
                  )}
                  {statusAprovacao === 'rejeitada' && (
                    <div className="text-red-700 mt-1">
                      Rejeitada por <strong>{aprovacao.rejeitada_por_nome}</strong>:{' '}
                      "{aprovacao.rejeitada_motivo}"
                    </div>
                  )}
                  {statusAprovacao === 'pendente' && aprovacao.solicitada_por && (
                    <div className="text-amber-700 mt-1">
                      Solicitada por <strong>{aprovacao.solicitada_por.nome}</strong> em{' '}
                      {aprovacao.solicitada_em ? new Date(aprovacao.solicitada_em).toLocaleString('pt-BR') : '-'}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex flex-col gap-1">
                {/* Botão Solicitar Aprovação (comprador) */}
                {!jaGerou && ['nao_solicitada', 'rejeitada'].includes(statusAprovacao) && (
                  <Button
                    size="sm"
                    className="bg-blue-600 hover:bg-blue-700"
                    onClick={handleSolicitarAprovacao}
                    disabled={aprovacaoLoading || totalEstimado <= 0}
                    data-testid="btn-solicitar-aprovacao"
                  >
                    {aprovacaoLoading ? (
                      <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                    ) : (
                      <FileText className="w-3 h-3 mr-1" />
                    )}
                    Solicitar Aprovação
                  </Button>
                )}

                {/* Concluir aprovação (item a item) + Rejeitar tudo (responsáveis) */}
                {statusAprovacao === 'pendente' && (sounEuAprovador || isAdminUser) && (
                  <>
                    <Button
                      size="sm"
                      className="bg-emerald-600 hover:bg-emerald-700"
                      onClick={handleConcluirAprovacao}
                      disabled={aprovacaoLoading}
                      data-testid="btn-concluir-aprovacao"
                    >
                      {aprovacaoLoading ? (
                        <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                      ) : (
                        <Check className="w-3 h-3 mr-1" />
                      )}
                      Concluir aprovação
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => setShowRejeitarDialog(true)}
                      disabled={aprovacaoLoading}
                      data-testid="btn-rejeitar-cotacao"
                    >
                      <X className="w-3 h-3 mr-1" />
                      Rejeitar tudo
                    </Button>
                  </>
                )}

                {/* Cancelar solicitação (apenas se pendente e for o solicitante) */}
                {statusAprovacao === 'pendente' && aprovacao.solicitada_por?.id === currentUserLocal.id && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleCancelarAprovacao}
                    disabled={aprovacaoLoading}
                    data-testid="btn-cancelar-solicitacao"
                  >
                    <RotateCcw className="w-3 h-3 mr-1" />
                    Cancelar Solicitação
                  </Button>
                )}
              </div>
            </div>
          </Card>
        )}

        {/* Painel de Fornecedores — Resumo + Score + Atalho "Aprovar Tudo" (migrado do antigo Comparar) */}
        {fornecedores.length > 0 && (
          <Card className="p-3 bg-gradient-to-r from-slate-50 to-amber-50/30 border-slate-200" data-testid="painel-fornecedores">
            <div className="flex items-center justify-between mb-2">
              <Label className="text-sm font-semibold flex items-center gap-2">
                <Award className="w-4 h-4 text-amber-500" />
                Fornecedores nesta cotação
              </Label>
              {scores && scores.length > 0 && (
                <Button size="sm" variant="outline" onClick={recalcularScore} disabled={loadingScore} className="gap-1.5 text-xs h-7" data-testid="btn-recalcular-score">
                  {loadingScore ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                  Recalcular Score
                </Button>
              )}
            </div>
            <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(auto-fit, minmax(220px, 1fr))` }}>
              {fornecedores.map(forn => {
                const s = scores?.find(x => x.fornecedor_id === forn.fornecedor_id);
                const isBest = s && s.score === melhorScore && s.score > 0;
                const total = forn.resposta?.total || forn.resposta?.valor_total || 0;
                const prazo = forn.resposta?.prazo_entrega;
                const condPag = forn.resposta?.condicao_pagamento;
                const catCount = (forn.resposta?.catalogos || []).length;
                const anxCount = (forn.resposta?.anexos_avulsos || []).length;
                const freteMin = Number(forn.resposta?.frete_minimo) || 0;
                const freteTipo = forn.resposta?.frete_tipo;
                const freteValor = Number(forn.resposta?.frete_valor) || 0;
                const impostos = Number(forn.resposta?.valor_impostos) || 0;
                const impostosInclusos = !!forn.resposta?.impostos_inclusos;
                const desconto = Number(forn.resposta?.valor_desconto || forn.resposta?.desconto) || 0;
                const garantia = forn.resposta?.prazo_garantia;
                const assistencia = forn.resposta?.assistencia_tecnica;
                const formaPag = forn.resposta?.forma_pagamento;
                const incotermForn = forn.resposta?.incoterm || cotacao?.incoterm || '';
                const transitForn = forn.resposta?.transit_time_dias ?? cotacao?.transit_time_dias ?? null;
                const pesoBrutoForn = Number(forn.resposta?.peso_bruto) || 0;
                const cbmForn = Number(forn.resposta?.volume_cbm) || 0;
                const observacao = forn.resposta?.observacao;
                return (
                  <div
                    key={forn.fornecedor_id}
                    className={`rounded-lg border p-2.5 bg-white transition-all ${isBest ? 'border-emerald-300 ring-2 ring-emerald-200' : 'border-slate-200'}`}
                    data-testid={`card-forn-${forn.fornecedor_id}`}
                  >
                    <div className="flex items-start justify-between gap-2 mb-1.5">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          {isBest && <Trophy className="h-3.5 w-3.5 text-amber-500 shrink-0" />}
                          <span className="text-xs font-semibold text-slate-800 truncate" title={forn.fornecedor_nome}>
                            {forn.fornecedor_nome}
                          </span>
                        </div>
                        {/* Histórico do fornecedor (inteligência do negócio) */}
                        {(() => {
                          const intel = intelFornecedores[forn.fornecedor_id];
                          if (!intel) return null;
                          const atraso = intel.atraso_medio_dias;
                          return (
                            <div className="flex items-center gap-1 flex-wrap mt-1" data-testid={`intel-badge-${forn.fornecedor_id}`}>
                              {atraso != null && atraso > 2 && (
                                <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 font-medium" title={`Histórico: entrega em média ${Math.round(atraso)} dias após o prometido (${intel.entregas_medidas} entrega(s) medida(s))`}>
                                  ⏱ atrasa ~{Math.round(atraso)}d
                                </span>
                              )}
                              {atraso != null && atraso <= 2 && (
                                <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium" title="Histórico: entrega dentro do prazo prometido">
                                  ⏱ pontual
                                </span>
                              )}
                              {intel.tendencia_preco_pct != null && intel.tendencia_preco_pct >= 3 && (
                                <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-700 font-medium" title="Tendência de preço dos mesmos produtos nos últimos 12 meses">
                                  ↑ preços +{intel.tendencia_preco_pct}%
                                </span>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                      {s && (
                        <div className="text-right shrink-0">
                          <div
                            className="text-lg font-bold leading-none"
                            style={{ color: s.score >= 80 ? '#059669' : s.score >= 60 ? '#d97706' : '#dc2626' }}
                            title={`Score: ${s.score.toFixed(1)} / 100`}
                          >
                            {s.score.toFixed(0)}
                          </div>
                          <div className="text-[9px] text-slate-400">score</div>
                        </div>
                      )}
                    </div>
                    <div className="text-[11px] space-y-0.5 text-slate-600">
                      <div className="flex items-center justify-between">
                        <span className="text-slate-400">Total cotado:</span>
                        <span className="font-semibold text-slate-800">{formatCurrency(total)}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-slate-400">Prazo entrega:</span>
                        <span className={`truncate ml-1 text-right ${prazo ? '' : 'text-slate-300 italic'}`} title={prazo || 'Não informado'}>
                          {prazo || '—'}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-slate-400">Cond. pagto:</span>
                        <span className={`truncate ml-1 text-right ${condPag ? '' : 'text-slate-300 italic'}`} title={condPag || 'Não informado'}>
                          {condPag || '—'}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-slate-400">Frete:</span>
                        <span className={`ml-1 text-right ${freteTipo ? '' : 'text-slate-300 italic'}`}>
                          {freteTipo === 'cif'
                            ? 'CIF (incluso)'
                            : freteTipo === 'fob'
                              ? `FOB ${freteValor > 0 ? formatCurrency(freteValor) : ''}`
                              : (freteTipo || '—')}
                        </span>
                      </div>
                      {freteMin > 0 && (
                        <div className="flex items-center justify-between text-amber-700">
                          <span>Frete mín:</span>
                          <span className="font-medium">{formatCurrency(freteMin)}</span>
                        </div>
                      )}
                      {incotermForn && (
                        <div className="flex items-center justify-between" data-testid={`forn-incoterm-${forn.fornecedor_id}`}>
                          <span className="text-slate-400">Incoterm:</span>
                          <span className="font-medium uppercase">{incotermForn}</span>
                        </div>
                      )}
                      {transitForn > 0 && (
                        <div className="flex items-center justify-between" data-testid={`forn-transit-${forn.fornecedor_id}`}>
                          <span className="text-slate-400">Transit time:</span>
                          <span>{transitForn} dia(s)</span>
                        </div>
                      )}
                      {pesoBrutoForn > 0 && (
                        <div className="flex items-center justify-between" data-testid={`forn-peso-${forn.fornecedor_id}`}>
                          <span className="text-slate-400">Peso bruto:</span>
                          <span>{pesoBrutoForn.toLocaleString('pt-BR', { maximumFractionDigits: 2 })} kg</span>
                        </div>
                      )}
                      {cbmForn > 0 && (
                        <div className="flex items-center justify-between" data-testid={`forn-cbm-${forn.fornecedor_id}`}>
                          <span className="text-slate-400">Volume (CBM):</span>
                          <span>{cbmForn.toLocaleString('pt-BR', { maximumFractionDigits: 4 })} m³</span>
                        </div>
                      )}
                      <div className="flex items-center justify-between">
                        <span className="text-slate-400">Garantia:</span>
                        <span className={`truncate ml-1 text-right ${garantia ? '' : 'text-slate-300 italic'}`} title={garantia || 'Não informada'}>
                          {garantia || '—'}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-slate-400">Assist. técnica:</span>
                        <span className={`truncate ml-1 text-right ${assistencia ? '' : 'text-slate-300 italic'}`} title={assistencia || 'Não informada'}>
                          {assistencia || '—'}
                        </span>
                      </div>
                      {impostos > 0 && (
                        <div className="flex items-center justify-between">
                          <span className="text-slate-400">Impostos:</span>
                          <span className="ml-1 text-right">
                            {formatCurrency(impostos)} {impostosInclusos ? <span className="text-[9px] text-slate-400">(inclusos)</span> : ''}
                          </span>
                        </div>
                      )}
                      {desconto > 0 && (
                        <div className="flex items-center justify-between text-rose-700">
                          <span>Desconto:</span>
                          <span className="font-medium">−{formatCurrency(desconto)}</span>
                        </div>
                      )}
                      {/* Landed Cost estimado = total + frete(FOB) + impostos(não inclusos) − desconto */}
                      {(() => {
                        const freteAdd = freteTipo === 'fob' ? freteValor : 0;
                        const impostosAdd = impostosInclusos ? 0 : impostos;
                        const landed = total + freteAdd + impostosAdd - desconto;
                        const taxa = Number(cotacao?.taxa_cambio) || 0;
                        const isBestLanded = landed > 0 && Math.abs(landed - menorLanded) < 0.005;
                        return (
                          <div
                            className={`flex items-center justify-between pt-1 mt-1 border-t border-slate-200 font-semibold ${isBestLanded ? 'text-emerald-700' : 'text-slate-900'}`}
                            data-testid={`landed-cost-${forn.fornecedor_id}`}
                            title="Custo total estimado: total dos itens + frete (FOB) + impostos não inclusos − desconto"
                          >
                            <span className="flex items-center gap-1">
                              {isBestLanded && <Trophy className="h-3 w-3 text-emerald-500" />}
                              Landed Cost:
                            </span>
                            <span className="text-right">
                              {formatCurrency(landed)}
                              {cotacao?.moeda && cotacao.moeda !== 'BRL' && taxa > 0 && (
                                <span className="block text-[9px] font-normal text-slate-400">≈ {_fmtCurrency(landed * taxa, 'BRL')}</span>
                              )}
                            </span>
                          </div>
                        );
                      })()}
                      {formaPag && formaPag !== condPag && (
                        <div className="flex items-center justify-between">
                          <span className="text-slate-400">Forma pgto:</span>
                          <span className="truncate ml-1 text-right" title={formaPag}>{formaPag}</span>
                        </div>
                      )}
                      {observacao ? (
                        <div className="pt-1 border-t border-slate-100 mt-1">
                          <div className="text-slate-400 text-[10px] mb-0.5">Obs.:</div>
                          <div
                            className="text-[10px] text-slate-700 italic line-clamp-2"
                            title={observacao}
                            data-testid={`obs-forn-${forn.fornecedor_id}`}
                          >
                            &quot;{observacao}&quot;
                          </div>
                        </div>
                      ) : null}
                    </div>
                    {(catCount > 0 || anxCount > 0) && (
                      <div className="flex items-center gap-1 mt-1.5">
                        {catCount > 0 && (
                          <span className="text-[9px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200" title={`${catCount} catálogo(s)`}>
                            📕 {catCount}
                          </span>
                        )}
                        {anxCount > 0 && (
                          <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200" title={`${anxCount} anexo(s)`}>
                            📎 {anxCount}
                          </span>
                        )}
                      </div>
                    )}
                    <div className="flex items-center gap-1 mt-2">
                      <button
                        type="button"
                        onClick={() => setPdfPreview({
                          path: `/cotacoes/${cotacao.id}/resposta/${forn.fornecedor_id}/pdf`,
                          filename: `Resposta_${cotacao.numero}_${(forn.fornecedor_nome||'fornecedor').replace(/\s+/g,'_')}.pdf`,
                          title: `Resposta — ${forn.fornecedor_nome} · ${cotacao.numero}`,
                        })}
                        title="Visualizar PDF da resposta"
                        className="text-[10px] text-blue-600 hover:text-blue-800 hover:underline inline-flex items-center gap-0.5"
                        data-testid={`btn-pdf-forn-${forn.fornecedor_id}`}
                      >
                        <FileText className="w-3 h-3" /> Ver PDF
                      </button>
                      {!resumo.completo && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="ml-auto h-6 px-2 text-[10px] border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                          onClick={() => handleAprovarTudoFornecedor(forn.fornecedor_id, forn.fornecedor_nome)}
                          data-testid={`btn-aprovar-tudo-${forn.fornecedor_id}`}
                          title="Premiar todos os itens disponíveis para este fornecedor"
                        >
                          <Check className="w-3 h-3 mr-0.5" /> {jaGerou ? 'Aprovar restantes' : 'Aprovar tudo'}
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Toggles de avaliação manual + breakdown */}
            {(criteriosManuais.length > 0 || (scores && scores.length > 0)) && (
              <div className="mt-3 pt-2 border-t border-slate-200 flex flex-wrap gap-3">
                {criteriosManuais.length > 0 && (
                  <button
                    onClick={() => setShowCriteriosManuais(v => !v)}
                    className="flex items-center gap-1 text-[11px] text-blue-600 hover:text-blue-800 font-medium"
                    data-testid="btn-toggle-criterios-manuais"
                  >
                    {showCriteriosManuais ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                    {showCriteriosManuais ? 'Ocultar avaliação manual' : 'Avaliar critérios manuais'}
                  </button>
                )}
                {scores && scores.length > 0 && (
                  <button
                    onClick={() => setShowBreakdown(v => !v)}
                    className="flex items-center gap-1 text-[11px] text-slate-500 hover:text-slate-700"
                    data-testid="btn-toggle-breakdown"
                  >
                    {showBreakdown ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                    {showBreakdown ? 'Ocultar detalhamento por critério' : 'Ver detalhamento por critério'}
                  </button>
                )}
              </div>
            )}

            {/* Tabela de avaliação manual */}
            {showCriteriosManuais && criteriosManuais.length > 0 && (
              <div className="mt-3 overflow-x-auto" data-testid="tabela-criterios-manuais">
                <table className="w-full text-xs border-collapse">
                  <thead>
                    <tr className="bg-slate-100">
                      <th className="text-left p-2 font-semibold text-slate-600 border-b min-w-[160px]">Critério</th>
                      {fornecedores.map(f => (
                        <th key={f.fornecedor_id} className="text-center p-2 font-semibold text-slate-600 border-b min-w-[140px]">{f.fornecedor_nome}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {criteriosManuais.map(crit => (
                      <tr key={crit.id} className="border-b hover:bg-slate-50">
                        <td className="p-2 font-medium text-slate-700">
                          {crit.nome}
                          <span className="text-slate-400 ml-1">(peso {crit.peso})</span>
                        </td>
                        {fornecedores.map(f => (
                          <td key={f.fornecedor_id} className="p-2 text-center">
                            <Select
                              value={String(avaliacoes[f.fornecedor_id]?.[crit.id] ?? '')}
                              onValueChange={v => updateAvaliacao(f.fornecedor_id, crit.id, parseFloat(v))}
                            >
                              <SelectTrigger className="h-7 text-[11px]"><SelectValue placeholder="Selecione" /></SelectTrigger>
                              <SelectContent>
                                {(crit.opcoes || []).map(op => (
                                  <SelectItem key={op.valor} value={String(op.valor)}>{op.label} ({op.valor})</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="flex justify-end mt-2">
                  <Button size="sm" onClick={recalcularScore} disabled={loadingScore} className="gap-1.5 bg-amber-500 hover:bg-amber-600 text-xs h-7">
                    {loadingScore ? <Loader2 className="h-3 w-3 animate-spin" /> : <Award className="h-3 w-3" />}
                    Atualizar Score
                  </Button>
                </div>
              </div>
            )}

            {/* Breakdown */}
            {showBreakdown && scores && scores.length > 0 && (
              <div className="mt-3 overflow-x-auto" data-testid="tabela-breakdown-score">
                <table className="w-full text-[11px] border-collapse">
                  <thead>
                    <tr className="bg-slate-50">
                      <th className="text-left p-1.5 font-semibold text-slate-500 border-b">Critério (Peso)</th>
                      {scores.map(s => (
                        <th key={s.fornecedor_id} className="text-center p-1.5 font-semibold text-slate-500 border-b">{s.fornecedor_nome}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {criteriosAtivos.map(b => (
                      <tr key={b.criterio_id} className="border-b">
                        <td className="p-1.5 text-slate-600">{b.criterio_nome} <span className="text-slate-300">(x{b.peso})</span></td>
                        {scores.map(s => {
                          const bd = s.breakdown.find(x => x.criterio_id === b.criterio_id);
                          const nota = bd?.nota;
                          return (
                            <td key={s.fornecedor_id} className="p-1.5 text-center">
                              {nota !== null && nota !== undefined ? (
                                <span className={`font-bold ${nota >= 8 ? 'text-emerald-600' : nota >= 5 ? 'text-amber-600' : 'text-red-500'}`}>
                                  {nota.toFixed(1)}
                                </span>
                              ) : (
                                <span className="text-slate-300">-</span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                    <tr className="bg-slate-50 font-bold">
                      <td className="p-1.5 text-slate-700">SCORE FINAL</td>
                      {scores.map(s => (
                        <td key={s.fornecedor_id} className="p-1.5 text-center">
                          <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-white text-[10px] font-bold"
                            style={{ background: s.score >= 80 ? '#10b981' : s.score >= 60 ? '#f59e0b' : '#ef4444' }}
                          >
                            <Award className="h-2.5 w-2.5" />{s.score.toFixed(0)}
                          </span>
                        </td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        )}

        {/* Aviso pedidos já gerados */}
        {jaGerou && (
          <Card className="p-3 bg-emerald-50 border-emerald-300" data-testid="aviso-pedidos-gerados">
            <div className="flex items-start gap-2">
              <Check className="w-5 h-5 text-emerald-600 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-emerald-900">
                  {pedidosGerados.length} pedido(s) gerado(s) até agora
                  {resumo.pendentes > 0 && ` · ${resumo.pendentes} item(ns) ainda em aberto`}
                </p>
                <p className="text-xs text-emerald-700 mt-0.5">
                  Itens com pedido gerado estão travados. {resumo.pendentes > 0 ? 'Decida os itens restantes e gere a próxima onda.' : 'Cotação completa.'}
                </p>
              </div>
            </div>
          </Card>
        )}

        {/* Alertas de frete mínimo (Fase 4.4a) */}
        {!jaGerou && alertasFreteMinimo.length > 0 && (
          <Card className="p-3 bg-amber-50 border-amber-300" data-testid="alerta-frete-minimo">
            <div className="flex items-start gap-2">
              <Truck className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
              <div className="flex-1 space-y-1.5">
                <p className="text-sm font-semibold text-amber-900">
                  ⚠️ Valor de pedido mínimo não atingido em {alertasFreteMinimo.length} fornecedor(es)
                </p>
                {alertasFreteMinimo.map(a => (
                  <div key={a.fornecedor_id} className="text-xs text-amber-800 bg-white/60 rounded px-2 py-1.5 border border-amber-200">
                    <div>
                      <strong>{a.fornecedor_nome}</strong>: selecionado {formatCurrency(a.subtotal)} — mínimo exigido {formatCurrency(a.frete_minimo)} (faltam <strong>{formatCurrency(a.falta)}</strong>)
                    </div>
                    {a.sugestao && (
                      <button
                        type="button"
                        onClick={() => {
                          const info = precosPorItem[a.sugestao.item.id]?.[a.fornecedor_id];
                          if (info) {
                            aplicarDecisao(a.sugestao.item_idx, a.fornecedor_id, a.fornecedor_nome, info.preco_unitario);
                          }
                        }}
                        className="text-blue-700 hover:underline mt-0.5"
                        data-testid={`btn-sugestao-frete-${a.fornecedor_id}`}
                      >
                        💡 Sugestão: adicionar &quot;{a.sugestao.item.descricao}&quot; — diferença de apenas {formatCurrency(a.sugestao.dif)} vs melhor preço
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </Card>
        )}

        {/* Matriz: itens × fornecedores */}
        {itens.length === 0 ? (
          <p className="text-center text-gray-500 py-8">Nenhum item nesta cotação.</p>
        ) : fornecedores.length === 0 ? (
          <p className="text-center text-gray-500 py-8">Aguardando respostas dos fornecedores.</p>
        ) : (
          <div className="overflow-x-auto border rounded-lg" data-testid="matriz-decisoes">
            <table className="w-full text-sm">
              <thead className="bg-slate-100 sticky top-0">
                <tr>
                  <th className="text-left p-2 font-semibold border-b min-w-[260px]">Item</th>
                  <th className="text-center p-2 font-semibold border-b w-16">Qtd</th>
                  {fornecedores.map(f => (
                    <th key={f.fornecedor_id} className="text-center p-2 font-semibold border-b border-l min-w-[140px]">
                      <div className="truncate" title={f.fornecedor_nome}>{f.fornecedor_nome}</div>
                      {f.resposta?.frete_valor > 0 && (
                        <div className="text-[10px] font-normal text-gray-500 flex items-center justify-center gap-1 mt-0.5">
                          <Truck className="w-3 h-3" /> {formatCurrency(f.resposta.frete_valor)}
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={() => setPdfPreview({
                          path: `/cotacoes/${cotacao.id}/resposta/${f.fornecedor_id}/pdf`,
                          filename: `Resposta_${cotacao.numero}_${(f.fornecedor_nome||'fornecedor').replace(/\s+/g,'_')}.pdf`,
                          title: `Resposta — ${f.fornecedor_nome} · ${cotacao.numero}`,
                        })}
                        title="Visualizar PDF da resposta deste fornecedor"
                        className="text-[10px] mt-0.5 text-blue-600 hover:text-blue-800 hover:underline inline-flex items-center gap-0.5"
                        data-testid={`btn-pdf-resposta-mesa-${f.fornecedor_id}`}
                      >
                        <FileText className="w-3 h-3" /> Ver PDF
                      </button>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {itens.map((item, idx) => {
                  const decisao = getDecisaoDoItem(idx);
                  const best = melhorPorItem[item.id];
                  const isSplit = !!decisao?.is_split;
                  const splitFornIds = isSplit
                    ? new Set((decisao.splits || []).map(s => s.fornecedor_vencedor_id))
                    : new Set();
                  // Item com pedido gerado em onda anterior → locked
                  // BUG FIX: cellDisabled deve depender APENAS do item específico, não da cotação inteira.
                  // Em status aprovada_parcial, itens novos (adicionados após gerar onda 1) devem permanecer editáveis.
                  const temPedidoGerado = !!(item.pedido_gerado_id || (item.pedidos_gerados_ids && item.pedidos_gerados_ids.length > 0));
                  const cellDisabled = temPedidoGerado;
                  return (
                    <tr key={item.id || idx} className={`border-b hover:bg-slate-50/50 ${temPedidoGerado ? 'bg-slate-50/80' : ''}`}>
                      <td className="p-2">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="font-medium truncate max-w-[220px]" title={item.descricao}>
                              {item.descricao}
                            </div>
                            {(item.marca || item.modelo) && (
                              <div className="text-[10px] text-gray-500">
                                {[item.marca, item.modelo].filter(Boolean).join(' / ')}
                              </div>
                            )}
                          </div>
                          {!cellDisabled && (
                            <button
                              type="button"
                              onClick={() => abrirSplit(idx)}
                              title="Dividir entre 2 ou mais fornecedores"
                              className="text-amber-600 hover:text-amber-800 text-[10px] flex items-center gap-0.5 shrink-0"
                              data-testid={`btn-split-${idx}`}
                            >
                              <Split className="w-3 h-3" /> dividir
                            </button>
                          )}
                        </div>
                        {temPedidoGerado && (
                          <Badge variant="outline" className="mt-1 text-[10px] bg-blue-50 text-blue-700 border-blue-300" data-testid={`item-locked-${idx}`}>
                            🔒 pedido gerado · onda anterior
                          </Badge>
                        )}
                        {!temPedidoGerado && decisao && !isSplit && (
                          <Badge variant="outline" className="mt-1 text-[10px] bg-emerald-50 text-emerald-700 border-emerald-300">
                            ✓ {decisao.fornecedor_vencedor_nome}
                          </Badge>
                        )}
                        {!temPedidoGerado && isSplit && (
                          <div className="mt-1 flex flex-wrap gap-1">
                            {(decisao.splits || []).map((s, sIdx) => (
                              <Badge
                                key={sIdx}
                                variant="outline"
                                className="text-[10px] bg-amber-50 text-amber-700 border-amber-300"
                                title={`${s.quantidade} un × ${formatCurrency(s.preco_final)}`}
                              >
                                ⤴ {s.fornecedor_vencedor_nome}: {s.quantidade}un
                              </Badge>
                            ))}
                          </div>
                        )}
                        {/* Status da aprovação do item */}
                        {item.aprovacao_item?.status === 'aprovado' && (
                          <Badge className="mt-1 text-[10px] bg-violet-100 text-violet-700 border-violet-300" data-testid={`item-aprovado-${idx}`}>✓ Aprovado pelo aprovador</Badge>
                        )}
                        {item.aprovacao_item?.status === 'rejeitado' && (
                          <div className="mt-1">
                            <Badge className="text-[10px] bg-red-100 text-red-700 border-red-300" data-testid={`item-rejeitado-${idx}`} title={item.aprovacao_item?.motivo}>✕ Rejeitado: {item.aprovacao_item?.motivo}</Badge>
                          </div>
                        )}
                        {item.descartado && (
                          <Badge className="mt-1 text-[10px] bg-zinc-100 text-zinc-500 border-zinc-300" data-testid={`item-descartado-${idx}`}>Descartado</Badge>
                        )}
                        {/* Controles do APROVADOR (item a item) durante aprovação pendente */}
                        {statusAprovacao === 'pendente' && (sounEuAprovador || isAdminUser) && decisao && !temPedidoGerado && !item.descartado && (
                          <div className="mt-1.5 flex items-center gap-1" data-testid={`aprovador-controles-${idx}`}>
                            <button
                              type="button"
                              onClick={() => handleDecisaoItemAprovador(idx, 'aprovar')}
                              className={`text-[10px] px-1.5 py-0.5 rounded border ${item.aprovacao_item?.status === 'aprovado' ? 'bg-violet-600 text-white border-violet-600' : 'bg-white text-violet-700 border-violet-300 hover:bg-violet-50'}`}
                              data-testid={`btn-aprovar-item-${idx}`}
                            >Aprovar</button>
                            <button
                              type="button"
                              onClick={() => setRejeitarItem({ idx, motivo: '' })}
                              className={`text-[10px] px-1.5 py-0.5 rounded border ${item.aprovacao_item?.status === 'rejeitado' ? 'bg-red-600 text-white border-red-600' : 'bg-white text-red-700 border-red-300 hover:bg-red-50'}`}
                              data-testid={`btn-rejeitar-item-${idx}`}
                            >Rejeitar</button>
                          </div>
                        )}
                        {/* Remediação do COMPRADOR em item rejeitado */}
                        {['aprovada_parcial', 'rejeitada'].includes(statusAprovacao) && item.aprovacao_item?.status === 'rejeitado' && !item.descartado && (
                          <div className="mt-1.5 flex items-center gap-1" data-testid={`remediar-controles-${idx}`}>
                            <button
                              type="button"
                              onClick={() => handleRemediarItem(idx, 'redecidir')}
                              className="text-[10px] px-1.5 py-0.5 rounded border bg-white text-blue-700 border-blue-300 hover:bg-blue-50"
                              data-testid={`btn-redecidir-item-${idx}`}
                            >Re-decidir</button>
                            <button
                              type="button"
                              onClick={() => handleRemediarItem(idx, 'descartar')}
                              className="text-[10px] px-1.5 py-0.5 rounded border bg-white text-zinc-600 border-zinc-300 hover:bg-zinc-50"
                              data-testid={`btn-descartar-item-${idx}`}
                            >Descartar</button>
                          </div>
                        )}
                      </td>
                      <td className="p-2 text-center">{item.quantidade}</td>
                      {fornecedores.map(forn => {
                        const info = precosPorItem[item.id]?.[forn.fornecedor_id];
                        const isVencedor = !isSplit && decisao?.fornecedor_vencedor_id === forn.fornecedor_id;
                        const isSplitParticipante = isSplit && splitFornIds.has(forn.fornecedor_id);
                        const isBest = best?.fornecedor_id === forn.fornecedor_id && info;
                        return (
                          <td key={forn.fornecedor_id} className="border-l p-1 text-center align-middle">
                            {!info ? (
                              <div className="text-[10px] text-gray-300 italic">N/Cot.</div>
                            ) : (
                              <TooltipProvider delayDuration={400}>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <button
                                      type="button"
                                      disabled={cellDisabled}
                                      onClick={() => !isSplit && !cellDisabled && handleSelecionar(idx, forn.fornecedor_id)}
                                      data-testid={`cell-${idx}-${forn.fornecedor_id}`}
                                      className={`w-full px-2 py-1.5 rounded transition-all border ${
                                        isVencedor
                                          ? 'bg-emerald-500 text-white border-emerald-600 shadow-sm font-semibold'
                                          : isSplitParticipante
                                            ? 'bg-amber-100 border-amber-300 text-amber-900'
                                            : isBest
                                              ? 'bg-emerald-50 border-emerald-200 text-emerald-800 hover:bg-emerald-100'
                                              : 'bg-white border-slate-200 hover:bg-slate-100'
                                      } ${cellDisabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
                                    >
                                      {info.equivalente_adotado && info.equivalente && (
                                        <div
                                          className="text-[8px] font-semibold text-emerald-700 bg-emerald-100 rounded px-1 py-px mb-0.5 truncate"
                                          data-testid={`equiv-tag-${idx}-${forn.fornecedor_id}`}
                                          title={`Equivalente: ${info.equivalente.produto_nome || info.equivalente.descricao}`}
                                        >
                                          ✓ Equiv: {info.equivalente.produto_nome || info.equivalente.descricao}
                                        </div>
                                      )}
                                      {info.descricao_alternativa && !info.equivalente_adotado && (
                                        <div
                                          className="text-[8px] font-semibold text-orange-700 bg-orange-100 rounded px-1 py-px mb-0.5 truncate"
                                          data-testid={`alt-tag-${idx}-${forn.fornecedor_id}`}
                                          title={`Alternativa: ${info.descricao_alternativa}`}
                                        >
                                          💡 Alt. sugerida
                                        </div>
                                      )}
                                      <div className="font-medium text-xs">
                                        {formatCurrency(info.preco_unitario)}
                                      </div>
                                      <div className="text-[9px] opacity-80">
                                        Total: {formatCurrency(info.preco_unitario * item.quantidade)}
                                      </div>
                                      {isBest && !isVencedor && !isSplitParticipante && !temPedidoGerado && (
                                        <div className="text-[9px] text-emerald-600 font-semibold mt-0.5">★ melhor</div>
                                      )}
                                      {isVencedor && <Check className="w-3 h-3 mx-auto mt-0.5" />}
                                      {isSplitParticipante && <Split className="w-3 h-3 mx-auto mt-0.5" />}
                                    </button>
                                  </TooltipTrigger>
                                  <TooltipContent side="top" className="text-xs max-w-xs">
                                    {info.equivalente_adotado && info.equivalente && (
                                      <div className="mb-1 pb-1 border-b border-slate-500">
                                        <div className="font-semibold text-emerald-300">✓ Equivalente adotado:</div>
                                        {info.equivalente.produto_nome && (
                                          <div><strong>{info.equivalente.produto_nome}</strong></div>
                                        )}
                                        {info.equivalente.descricao && info.equivalente.descricao !== info.equivalente.produto_nome && (
                                          <div className="text-[10px] opacity-80">{info.equivalente.descricao}</div>
                                        )}
                                        <div className="text-[10px] opacity-70 italic mt-0.5">
                                          Item original: "{item.descricao}"
                                        </div>
                                      </div>
                                    )}
                                    {info.descricao_alternativa && !info.equivalente_adotado && (
                                      <div className="mb-1 pb-1 border-b border-slate-500">
                                        <div className="font-semibold text-orange-300">💡 Alternativa sugerida:</div>
                                        <div>{info.descricao_alternativa}</div>
                                        {info.marca_alternativa && <div className="text-[10px] opacity-80">Marca: {info.marca_alternativa}</div>}
                                        {info.motivo_alternativa && <div className="text-[10px] italic opacity-80">Motivo: {info.motivo_alternativa}</div>}
                                      </div>
                                    )}
                                    {info.prazo_dias && <div>Prazo: {info.prazo_dias} dias</div>}
                                    {temPedidoGerado
                                      ? <div>Item travado — pedido já gerado em onda anterior</div>
                                      : isSplit
                                        ? <div>Item em split — clique &quot;dividir&quot; para editar</div>
                                        : isVencedor ? <div className="font-semibold">Clique p/ remover</div> : <div>Clique p/ escolher</div>}
                                    {info.descricao_alternativa && !cellDisabled && (
                                      <div className="mt-1 pt-1 border-t border-slate-500 text-orange-300">
                                        Clique &quot;Avaliar&quot; abaixo para adotar a sugestão
                                      </div>
                                    )}
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )}
                            {info?.descricao_alternativa && !cellDisabled && (
                              <button
                                type="button"
                                onClick={() => setAvaliarAlternativa({
                                  itemIdx: idx,
                                  item,
                                  fornecedor: forn,
                                  info,
                                })}
                                className={`mt-1 text-[9px] hover:underline font-medium ${info.equivalente_adotado ? 'text-emerald-700 hover:text-emerald-900' : 'text-orange-700 hover:text-orange-900'}`}
                                data-testid={`btn-avaliar-alt-${idx}-${forn.fornecedor_id}`}
                              >
                                {info.equivalente_adotado ? 'Re-avaliar equivalente' : 'Avaliar sugestão'}
                              </button>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Lista de pedidos gerados — agrupados por onda */}
        {jaGerou && (
          <Card className="p-4 mt-2" data-testid="lista-pedidos-gerados">
            <Label className="text-sm font-semibold mb-2 block flex items-center gap-2">
              <Package className="w-4 h-4" /> Pedidos gerados
              {(cotacao.lotes_gerados?.length || 0) > 1 && (
                <span className="text-xs font-normal text-gray-500">
                  · {cotacao.lotes_gerados.length} ondas
                </span>
              )}
            </Label>
            {(cotacao.lotes_gerados && cotacao.lotes_gerados.length > 0) ? (
              <div className="space-y-3">
                {cotacao.lotes_gerados.map((lote, idx) => (
                  <div key={lote.lote_split_id} className="border-l-2 border-emerald-400 pl-3" data-testid={`onda-${idx + 1}`}>
                    <div className="text-xs font-semibold text-emerald-700">
                      Onda {idx + 1} · {lote.total_pedidos} pedido(s) · {formatCurrency(lote.total_valor || 0)}
                    </div>
                    <div className="text-[10px] text-gray-500 mb-1">
                      {lote.criado_em && (() => {
                        try { return new Date(lote.criado_em).toLocaleString('pt-BR'); } catch { return ''; }
                      })()}
                      {lote.criado_por && ` · por ${lote.criado_por}`}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 mt-1">
                      {(lote.pedidos || []).map(p => (
                        <div key={p.id} className="border rounded p-2 bg-slate-50" data-testid={`pedido-resumo-${p.numero}`}>
                          <div className="font-mono text-xs text-blue-700">{p.numero}</div>
                          <div className="text-sm font-medium truncate">{p.fornecedor_nome}</div>
                          <div className="text-xs text-gray-500">
                            {p.qtd_itens} item(ns) · {formatCurrency(p.total)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                {(cotacao.pedidos_gerados_resumo || []).map(p => (
                  <div key={p.id} className="border rounded p-2 bg-slate-50" data-testid={`pedido-resumo-${p.numero}`}>
                    <div className="font-mono text-xs text-blue-700">{p.numero}</div>
                    <div className="text-sm font-medium truncate">{p.fornecedor_nome}</div>
                    <div className="text-xs text-gray-500">
                      {p.qtd_itens} item(ns) · {formatCurrency(p.total)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        )}

        <DialogFooter className="border-t pt-3 mt-2">
          <Button variant="outline" onClick={onClose} data-testid="btn-fechar-mesa">
            Fechar
          </Button>
          {(resumo.decididos_sem_pedido_gerenciavel ?? resumo.decididos_sem_pedido ?? 0) > 0 && !aprovacaoBloqueiaPedidos && (
            <Button
              className="bg-emerald-600 hover:bg-emerald-700"
              onClick={handleGerarPedidos}
              disabled={generating}
              data-testid="btn-gerar-pedidos"
            >
              {generating ? (
                <Loader2 className="w-3 h-3 mr-1 animate-spin" />
              ) : (
                <FileText className="w-3 h-3 mr-1" />
              )}
              {resumo.pendentes > 0
                ? `Gerar pedidos parciais (${resumo.decididos_sem_pedido_gerenciavel ?? resumo.decididos_sem_pedido} item(ns) · ${resumo.pendentes} em aberto)`
                : `Gerar ${resumo.decididos_sem_pedido_gerenciavel ?? resumo.decididos_sem_pedido} pedido(s) finais`}
            </Button>
          )}
          {(resumo.decididos_sem_pedido_gerenciavel ?? resumo.decididos_sem_pedido ?? 0) > 0 && aprovacaoBloqueiaPedidos && (
            <Button disabled className="bg-amber-500/60" data-testid="btn-gerar-pedidos-bloqueado">
              <AlertTriangle className="w-3 h-3 mr-1" />
              {statusAprovacao === 'pendente' ? 'Aguardando aprovação...' :
               statusAprovacao === 'rejeitada' ? 'Aprovação rejeitada — solicite novamente' :
               'Requer aprovação antes de gerar pedidos'}
            </Button>
          )}
          {(resumo.decididos_sem_pedido_gerenciavel ?? resumo.decididos_sem_pedido ?? 0) === 0 && resumo.pendentes > 0 && (
            <Button disabled className="bg-slate-300">
              Decida pelo menos 1 item para gerar pedidos
            </Button>
          )}
        </DialogFooter>

        {/* Dialog de Rejeitar Cotação (com motivo) */}
        <Dialog open={showRejeitarDialog} onOpenChange={(o) => { if (!o) { setShowRejeitarDialog(false); setMotivoRejeicao(''); } }}>
          <DialogContent className="max-w-md" data-testid="dialog-rejeitar-cotacao">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <X className="w-5 h-5 text-red-500" />
                Rejeitar Aprovação
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <p className="text-sm text-gray-600">
                Informe o motivo da rejeição. O comprador será notificado.
              </p>
              <Textarea
                value={motivoRejeicao}
                onChange={(e) => setMotivoRejeicao(e.target.value)}
                placeholder="Ex: Preço acima do orçado, fornecedor sem histórico, etc."
                rows={4}
                data-testid="input-motivo-rejeicao"
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setShowRejeitarDialog(false); setMotivoRejeicao(''); }}>
                Cancelar
              </Button>
              <Button
                variant="destructive"
                onClick={handleRejeitarCotacao}
                disabled={aprovacaoLoading || motivoRejeicao.trim().length < 3}
                data-testid="btn-confirmar-rejeicao"
              >
                {aprovacaoLoading && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
                Confirmar Rejeição
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Modal de rejeição de ITEM (aprovador) */}
        <Dialog open={!!rejeitarItem} onOpenChange={(o) => { if (!o) setRejeitarItem(null); }}>
          <DialogContent className="max-w-md" data-testid="dialog-rejeitar-item">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <X className="w-5 h-5 text-red-500" />
                Rejeitar item
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <p className="text-sm text-gray-600">
                {rejeitarItem != null && itens[rejeitarItem.idx] ? (
                  <>Item: <strong>{itens[rejeitarItem.idx].descricao}</strong>. </>
                ) : null}
                Informe o motivo da rejeição. O comprador poderá <strong>re-decidir</strong> (outro fornecedor) ou <strong>descartar</strong> o item.
              </p>
              <Textarea
                value={rejeitarItem?.motivo || ''}
                onChange={(e) => setRejeitarItem((prev) => prev ? { ...prev, motivo: e.target.value } : prev)}
                placeholder="Ex: preço acima do orçado; prefiro outro fornecedor; especificação divergente..."
                rows={4}
                data-testid="input-motivo-rejeicao-item"
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setRejeitarItem(null)}>Cancelar</Button>
              <Button
                variant="destructive"
                onClick={handleConfirmarRejeitarItem}
                disabled={(rejeitarItem?.motivo || '').trim().length < 3}
                data-testid="btn-confirmar-rejeicao-item"
              >
                Rejeitar item
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>


        {/* Modal de motivo na troca de decisão */}
        <Dialog open={!!trocaDecisao} onOpenChange={(o) => { if (!o) { setTrocaDecisao(null); setMotivoTroca(''); } }}>          <DialogContent className="max-w-md" data-testid="dialog-motivo-troca">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-500" />
                Trocar decisão registrada
              </DialogTitle>
            </DialogHeader>
            {trocaDecisao && (
              <div className="space-y-3 text-sm">
                <p className="text-gray-600">
                  Você está trocando o fornecedor vencedor deste item. Por compliance, o motivo é obrigatório e será registrado em auditoria.
                </p>
                <div className="bg-slate-50 rounded p-2 text-xs space-y-1">
                  <div><strong>Decisão anterior:</strong> {trocaDecisao.decisao_atual.fornecedor_vencedor_nome} — {formatCurrency(trocaDecisao.decisao_atual.preco_final)}</div>
                  <div className="text-emerald-700"><strong>Nova decisão:</strong> {trocaDecisao.forn_nome} — {formatCurrency(trocaDecisao.preco_final)}</div>
                </div>
                <div>
                  <Label className="text-xs">Motivo da troca *</Label>
                  <Textarea
                    value={motivoTroca}
                    onChange={(e) => setMotivoTroca(e.target.value)}
                    placeholder="Ex: melhor prazo de entrega; condição de pagamento; histórico de qualidade..."
                    rows={3}
                    autoFocus
                    data-testid="input-motivo-troca"
                  />
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => { setTrocaDecisao(null); setMotivoTroca(''); }}>
                <X className="w-3 h-3 mr-1" /> Cancelar
              </Button>
              <Button onClick={confirmarTroca} disabled={!motivoTroca.trim()} data-testid="btn-confirmar-troca">
                <Check className="w-3 h-3 mr-1" /> Confirmar troca
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Modal de Split de Quantidade (Fase 4.4b) */}
        <Dialog open={!!splitItem} onOpenChange={(o) => { if (!o) fecharSplit(); }}>
          <DialogContent className="max-w-2xl" data-testid="dialog-split">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Split className="w-5 h-5 text-amber-500" />
                Dividir Quantidade entre Fornecedores
              </DialogTitle>
            </DialogHeader>
            {splitItem && (() => {
              const item = splitItem.item;
              const qtdTotal = Number(item.quantidade) || 0;
              const somaQtd = splitItem.splits.reduce((acc, s) => acc + (Number(s.quantidade) || 0), 0);
              const dif = qtdTotal - somaQtd;
              const valorTotalSplit = splitItem.splits.reduce(
                (acc, s) => acc + (Number(s.quantidade) || 0) * (Number(s.preco_final) || 0),
                0
              );
              return (
                <div className="space-y-3 text-sm">
                  <div className="bg-slate-50 rounded p-2 text-xs">
                    <div><strong>Item:</strong> {item.descricao}</div>
                    <div><strong>Quantidade total:</strong> {qtdTotal} {item.unidade || 'un'}</div>
                  </div>
                  <div className="space-y-2">
                    {splitItem.splits.map((s, idx) => {
                      const fornsQueQuoter = Object.keys(precosPorItem[item.id] || {});
                      const fornsValidos = fornecedores.filter(f => fornsQueQuoter.includes(f.fornecedor_id));
                      return (
                        <div key={idx} className="border rounded p-2 bg-white space-y-1" data-testid={`split-row-${idx}`}>
                          <div className="flex items-center gap-2">
                            <select
                              value={s.fornecedor_vencedor_id}
                              onChange={(e) => updateSplitRow(idx, 'fornecedor_vencedor_id', e.target.value)}
                              className="flex-1 text-xs border rounded px-2 py-1"
                              data-testid={`split-forn-${idx}`}
                            >
                              {fornsValidos.map(f => (
                                <option key={f.fornecedor_id} value={f.fornecedor_id}>
                                  {f.fornecedor_nome}
                                </option>
                              ))}
                            </select>
                            {splitItem.splits.length > 2 && (
                              <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => removeSplitRow(idx)} data-testid={`split-remove-${idx}`}>
                                <X className="w-3 h-3 text-red-500" />
                              </Button>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="flex-1">
                              <Label className="text-[10px] text-gray-500">Quantidade</Label>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={s.quantidade}
                                onChange={(e) => updateSplitRow(idx, 'quantidade', parseFloat(e.target.value) || 0)}
                                className="w-full text-sm border rounded px-2 py-1"
                                data-testid={`split-qty-${idx}`}
                              />
                            </div>
                            <div className="flex-1">
                              <Label className="text-[10px] text-gray-500">Preço unitário (R$)</Label>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={s.preco_final}
                                onChange={(e) => updateSplitRow(idx, 'preco_final', parseFloat(e.target.value) || 0)}
                                className="w-full text-sm border rounded px-2 py-1"
                                data-testid={`split-preco-${idx}`}
                              />
                            </div>
                            <div className="flex-1">
                              <Label className="text-[10px] text-gray-500">Subtotal</Label>
                              <div className="text-sm font-medium py-1">{formatCurrency((s.quantidade || 0) * (s.preco_final || 0))}</div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <Button type="button" size="sm" variant="outline" onClick={addSplitRow} data-testid="btn-add-split-row">
                    + Adicionar fornecedor
                  </Button>
                  <div className={`p-2 rounded text-xs flex items-center justify-between ${Math.abs(dif) < 0.01 ? 'bg-emerald-50 text-emerald-800' : 'bg-amber-50 text-amber-800'}`}>
                    <span>
                      Soma: <strong>{Number(somaQtd).toFixed(2).replace(/\.?0+$/, '') || '0'}</strong> de <strong>{qtdTotal}</strong>
                      {Math.abs(dif) >= 0.01 && (
                        <span className="ml-2 font-medium">
                          (diferença: {dif > 0 ? '+' : ''}{Number(dif).toFixed(2).replace(/\.?0+$/, '') || '0'})
                        </span>
                      )}
                    </span>
                    <span>Total split: <strong>{formatCurrency(valorTotalSplit)}</strong></span>
                  </div>
                  {(() => {
                    const decisaoAtual = getDecisaoDoItem(splitItem.item_idx);
                    const tinha = decisaoAtual && (decisaoAtual.fornecedor_vencedor_id || decisaoAtual.is_split);
                    if (!tinha) return null;
                    return (
                      <div>
                        <Label className="text-xs">Motivo da alteração *</Label>
                        <Textarea
                          value={motivoSplit}
                          onChange={(e) => setMotivoSplit(e.target.value)}
                          placeholder="Por que está alterando esta decisão? (compliance)"
                          rows={2}
                          data-testid="input-motivo-split"
                        />
                      </div>
                    );
                  })()}
                </div>
              );
            })()}
            <DialogFooter>
              <Button variant="outline" onClick={fecharSplit}>Cancelar</Button>
              <Button
                onClick={confirmarSplit}
                disabled={!splitItem || Math.abs((Number(splitItem.item.quantidade) || 0) - splitItem.splits.reduce((a, s) => a + (Number(s.quantidade) || 0), 0)) > 0.01}
                data-testid="btn-confirmar-split"
              >
                <Check className="w-3 h-3 mr-1" /> Aplicar split
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* PDF Preview Dialog */}
        {pdfPreview && (
          <PdfPreviewDialog
            open={!!pdfPreview}
            onClose={() => setPdfPreview(null)}
            title={pdfPreview.title}
            apiPath={pdfPreview.path}
            filename={pdfPreview.filename}
          />
        )}

        {/* Avaliar Alternativa Dialog */}
        <AvaliarAlternativaDialog
          avaliar={avaliarAlternativa}
          cotacaoId={cotacao?.id}
          onClose={() => setAvaliarAlternativa(null)}
          onAdotado={() => { if (onReload) onReload(); fetchDecisoes(); }}
        />
      </DialogContent>
    </Dialog>
  );
};

export default MesaDecisao;
